/**
 * Ella Radio permanent never-play bans — GET/POST/DELETE /api/preferences/bans
 */

import { CLOUD_API_URL } from '../config/serverConfig';
import { api } from './api';
import { normalizePrimaryArtistKey } from '../radio/ellaRadioArtistMatch';

const CACHE_KEY = 'ella_radio_bans_cache';
const API_PROBE_KEY = 'ella_bans_api_available';

export interface BannedTrackRow {
  trackId: string;
  source: string;
  createdAt: string;
}

export interface BannedArtistRow {
  id: string;
  qobuzArtistId: string | null;
  musicBrainzId: string | null;
  nameKey: string;
  displayName: string;
  createdAt: string;
}

export interface EllaRadioBansCache {
  bannedTracks: BannedTrackRow[];
  bannedArtists: BannedArtistRow[];
  fetchedAt: number;
}

let memoryCache: EllaRadioBansCache | null = null;
let apiAvailableMemory: boolean | null = null;

function readLocalCache(): EllaRadioBansCache | null {
  if (memoryCache) return memoryCache;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EllaRadioBansCache;
    if (!parsed || !Array.isArray(parsed.bannedTracks) || !Array.isArray(parsed.bannedArtists)) return null;
    memoryCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalCache(cache: EllaRadioBansCache): void {
  memoryCache = cache;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore */
  }
}

export function getCachedEllaRadioBans(): EllaRadioBansCache {
  return readLocalCache() || { bannedTracks: [], bannedArtists: [], fetchedAt: 0 };
}

export function isEllaRadioBansApiAvailable(): boolean | null {
  if (apiAvailableMemory !== null) return apiAvailableMemory;
  try {
    const v = sessionStorage.getItem(API_PROBE_KEY);
    if (v === '1') return apiAvailableMemory = true;
    if (v === '0') return apiAvailableMemory = false;
  } catch {
    /* ignore */
  }
  return null;
}

export async function probeEllaRadioBansApi(): Promise<boolean> {
  const cached = isEllaRadioBansApiAvailable();
  if (cached !== null) return cached;

  try {
    const res = await api.raw('/api/preferences/bans');
    if (res.status === 404 || res.status === 405) {
      apiAvailableMemory = false;
      try { sessionStorage.setItem(API_PROBE_KEY, '0'); } catch { /* ignore */ }
      return false;
    }
    apiAvailableMemory = true;
    try { sessionStorage.setItem(API_PROBE_KEY, '1'); } catch { /* ignore */ }
    return true;
  } catch {
    apiAvailableMemory = false;
    try { sessionStorage.setItem(API_PROBE_KEY, '0'); } catch { /* ignore */ }
    return false;
  }
}

export async function fetchEllaRadioBans(): Promise<EllaRadioBansCache> {
  const data = await api.get('/api/preferences/bans') as {
    bannedTracks?: BannedTrackRow[];
    bannedArtists?: BannedArtistRow[];
  };
  const cache: EllaRadioBansCache = {
    bannedTracks: data.bannedTracks || [],
    bannedArtists: data.bannedArtists || [],
    fetchedAt: Date.now(),
  };
  writeLocalCache(cache);
  return cache;
}

export async function refreshEllaRadioBansOnStationCreate(): Promise<EllaRadioBansCache | null> {
  const available = await probeEllaRadioBansApi();
  if (!available) return null;
  try {
    return await fetchEllaRadioBans();
  } catch (e) {
    console.warn('[ellaRadioBans] refresh on station create failed:', e);
    return getCachedEllaRadioBans();
  }
}

export async function banEllaRadioTrack(trackId: string, source = 'qobuz'): Promise<EllaRadioBansCache> {
  const data = await api.post('/api/preferences/bans?type=track', {
    type: 'track',
    trackId: String(trackId),
    source,
  }) as { bannedTracks?: BannedTrackRow[]; bannedArtists?: BannedArtistRow[] };
  const cache: EllaRadioBansCache = {
    bannedTracks: data.bannedTracks || [],
    bannedArtists: data.bannedArtists || [],
    fetchedAt: Date.now(),
  };
  writeLocalCache(cache);
  return cache;
}

export async function banEllaRadioArtist(artistName: string, ids?: {
  qobuzArtistId?: string | null;
  musicBrainzId?: string | null;
}): Promise<EllaRadioBansCache> {
  const data = await api.post('/api/preferences/bans?type=artist', {
    type: 'artist',
    artistName,
    qobuzArtistId: ids?.qobuzArtistId ?? null,
    musicBrainzId: ids?.musicBrainzId ?? null,
  }) as { bannedTracks?: BannedTrackRow[]; bannedArtists?: BannedArtistRow[] };
  const cache: EllaRadioBansCache = {
    bannedTracks: data.bannedTracks || [],
    bannedArtists: data.bannedArtists || [],
    fetchedAt: Date.now(),
  };
  writeLocalCache(cache);
  return cache;
}

async function deleteBanRequest(body: Record<string, unknown>): Promise<EllaRadioBansCache> {
  const res = await api.raw('/api/preferences/bans', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `DELETE ban failed (${res.status})`);
  }
  const data = await res.json() as { bannedTracks?: BannedTrackRow[]; bannedArtists?: BannedArtistRow[] };
  const cache: EllaRadioBansCache = {
    bannedTracks: data.bannedTracks || [],
    bannedArtists: data.bannedArtists || [],
    fetchedAt: Date.now(),
  };
  writeLocalCache(cache);
  return cache;
}

export async function unbanEllaRadioTrack(trackId: string, source = 'qobuz'): Promise<EllaRadioBansCache> {
  return deleteBanRequest({ type: 'track', trackId: String(trackId), source });
}

export async function unbanEllaRadioArtist(params: {
  banId?: string;
  qobuzArtistId?: string | null;
  nameKey?: string;
}): Promise<EllaRadioBansCache> {
  return deleteBanRequest({
    type: 'artist',
    banId: params.banId,
    qobuzArtistId: params.qobuzArtistId ?? undefined,
    nameKey: params.nameKey ?? undefined,
  });
}

export function isTrackBannedLocally(trackId: string, source = 'qobuz', cache = getCachedEllaRadioBans()): boolean {
  const src = String(source || 'qobuz').toLowerCase();
  return cache.bannedTracks.some(
    (t) => t.trackId === String(trackId) && String(t.source || 'qobuz').toLowerCase() === src,
  );
}

export function isArtistBannedLocally(artistName: string, cache = getCachedEllaRadioBans()): boolean {
  const key = normalizePrimaryArtistKey(artistName);
  if (!key) return false;
  return cache.bannedArtists.some((a) => a.nameKey === key || a.displayName === artistName);
}
